import { CartAddEvent } from '@theme/events';

const DIALOG_ID = 'size-quick-add-dialog';
const MOBILE_MEDIA = typeof window !== 'undefined' ? window.matchMedia('(max-width: 749px)') : null;
const SELECTOR_TITLE = '.size-quick-add-modal__title';
const SELECTOR_PRICE = '.size-quick-add-modal__price';
const SELECTOR_SIZES = '[data-size-quick-add-sizes]';
const SELECTOR_LOADING = '[data-size-quick-add-loading]';
const SELECTOR_CLOSE = '[data-size-quick-add-close]';

function isMobile() {
  return MOBILE_MEDIA?.matches ?? false;
}

function getSizeOption(options) {
  if (!options?.length) return null;
  const sizeOpt = options.find((o) => String(o.name).toLowerCase().includes('size'));
  return sizeOpt || options[0] || null;
}

function getVariantForValue(variants, value, optionPosition = 1) {
  const key = `option${optionPosition}`;
  const v = variants.find((v) => v[key] === value);
  return v ? { id: v.id, available: v.available } : null;
}

/**
 * Format price with shop's money format (currency symbol and/or code).
 * @param {number} cents - Price in cents
 * @param {string} [moneyFormat] - e.g. "${{amount}}" or "{{amount}} AUD"
 * @param {string} [currency] - e.g. "AUD", "USD"
 * @returns {string}
 */
function formatPrice(cents, moneyFormat, currency) {
  const amount = (Number(cents) / 100).toFixed(2);
  const amountNoDecimals = Math.round(Number(cents) / 100).toString();
  if (moneyFormat && typeof moneyFormat === 'string') {
    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/gi, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/gi, amountNoDecimals)
      .replace(/\{\{\s*currency\s*\}\}/gi, currency || '')
      .trim();
  }
  if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
    return window.Shopify.formatMoney(cents);
  }
  return amount;
}

function fetchProduct(handle) {
  const root = (typeof window.Shopify !== 'undefined' && window.Shopify.routes?.root) || '/';
  return fetch(`${root}products/${handle}.js`).then((r) => r.json());
}

function init() {
  const dialog = /** @type {HTMLDialogElement | null} */ (document.getElementById(DIALOG_ID));
  if (!dialog) return;

  const titleEl = dialog.querySelector(SELECTOR_TITLE);
  const priceEl = dialog.querySelector(SELECTOR_PRICE);
  const sizesEl = dialog.querySelector(SELECTOR_SIZES);
  const loadingEl = dialog.querySelector(SELECTOR_LOADING);
  const closeBtn = dialog.querySelector(SELECTOR_CLOSE);

  if (!sizesEl) return;

  let currentHandle = null;

  function close() {
    try {
      dialog.close();
    } catch (_) {}
  }

  function showLoading(show) {
    if (loadingEl) loadingEl.hidden = !show;
    if (sizesEl) sizesEl.hidden = show;
    if (titleEl) titleEl.textContent = '';
    if (priceEl) priceEl.textContent = '';
  }

  function renderSizes(product) {
    const options = product.options || [];
    const sizeOption = getSizeOption(options);
    const variants = product.variants || [];

    sizesEl.innerHTML = '';
    if (!sizeOption) return;

    const optionPosition = sizeOption.position != null ? Number(sizeOption.position) : 1;
    const values = sizeOption.values || [];

    values.forEach((value) => {
      const variant = getVariantForValue(variants, value, optionPosition);
      const available = variant ? variant.available : false;
      const variantId = variant ? variant.id : null;

      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'size-quick-add-modal__size-btn';
      btn.disabled = !available;
      if (variantId) btn.dataset.variantId = String(variantId);
      btn.setAttribute('aria-label', available ? value : `${value} – Sold out`);
      if (!available) btn.setAttribute('aria-disabled', 'true');

      const label = document.createElement('span');
      label.className = 'size-quick-add-modal__size-btn-label';
      label.textContent = value;
      btn.append(label);

      const spinner = document.createElement('span');
      spinner.className = 'size-quick-add-modal__size-btn-spinner';
      spinner.setAttribute('aria-hidden', 'true');
      spinner.innerHTML =
        '<svg class="size-quick-add-modal__size-btn-spinner-svg" width="14" height="14" viewBox="0 0 66 66" fill="none" focusable="false"><circle class="size-quick-add-modal__size-btn-spinner-path" cx="33" cy="33" r="30" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round"/></svg>';
      btn.append(spinner);

      btn.addEventListener('click', async () => {
        const id = btn.dataset.variantId;
        if (!id || btn.disabled) return;
        btn.setAttribute('aria-busy', 'true');
        btn.classList.add('size-quick-add-modal__size-btn--loading');
        try {
          const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
            .map((el) => el.dataset?.sectionId)
            .filter(Boolean);
          const body = {
            items: [{ id: Number(id), quantity: 1 }],
            ...(sectionIds.length > 0 && { sections: sectionIds.join(',') }),
          };
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const data = await res.json();
          if (data.status && data.status !== 200) throw new Error(data.description || data.message);

          document.dispatchEvent(
            new CartAddEvent({}, 'size-quick-add-modal', {
              source: 'size-quick-add-modal',
              itemCount: 1,
              variantId: id,
              sections: data?.sections,
            })
          );
          close();
          const cartDrawer = document.querySelector('cart-drawer-component');
          if (cartDrawer?.hasAttribute('auto-open')) {
            setTimeout(() => {
              if (typeof cartDrawer.open === 'function') cartDrawer.open();
            }, 50);
          } else if (typeof cartDrawer?.open === 'function') {
            cartDrawer.open();
          }
        } catch (err) {
          document.dispatchEvent(
            new CartAddEvent({}, 'size-quick-add-modal', { didError: true, source: 'size-quick-add-modal' })
          );
          if (typeof window.Theme !== 'undefined' && window.Theme.translations?.add_to_cart_error) {
            console.warn(window.Theme.translations.add_to_cart_error, err?.message ?? err);
          }
        } finally {
          btn.removeAttribute('aria-busy');
          btn.classList.remove('size-quick-add-modal__size-btn--loading');
        }
      });

      li.append(btn);
      sizesEl.append(li);
    });
  }

  function openWithProduct(product) {
    if (!isMobile()) return;
    if (!product) return;

    const name = String(product.title ?? product.name ?? '').trim();
    const priceCents =
      product.price_min ??
      product.price ??
      product.variants?.[0]?.price ??
      0;
    const moneyFormat = dialog.getAttribute('data-money-format') || '';
    const currency = dialog.getAttribute('data-currency') || '';
    const priceFormatted = formatPrice(Number(priceCents), moneyFormat, currency);

    renderSizes(product);
    showLoading(false);
    dialog.showModal();

    requestAnimationFrame(() => {
      const titleNode = dialog.querySelector(SELECTOR_TITLE);
      const priceNode = dialog.querySelector(SELECTOR_PRICE);
      if (titleNode) {
        titleNode.textContent = name;
        titleNode.style.cssText = 'display:block;visibility:visible;color:#111;opacity:1;';
      }
      if (priceNode) {
        priceNode.textContent = priceFormatted;
        priceNode.style.cssText = 'display:block;visibility:visible;color:#111;opacity:1;';
      }
    });
  }

  function openWithHandle(handle) {
    if (!isMobile()) return;
    currentHandle = handle;
    showLoading(true);
    sizesEl.innerHTML = '';
    fetchProduct(handle)
      .then((res) => {
        if (currentHandle !== handle) return;
        const product = res && typeof res === 'object' && res.title != null ? res : (res && res.product ? res.product : null);
        openWithProduct(product);
      })
      .catch(() => {
        if (loadingEl) loadingEl.hidden = false;
        if (sizesEl) sizesEl.hidden = true;
      });
  }

  document.addEventListener('size-quick-add-modal:open', (/** @type {CustomEvent} */ e) => {
    const handle = e.detail?.handle;
    if (handle) openWithHandle(handle);
  });

  if (closeBtn) closeBtn.addEventListener('click', close);
  dialog.addEventListener('cancel', close);
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) close();
  });
}

function runInit() {
  if (document.getElementById(DIALOG_ID)) {
    init();
    return;
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInit);
  }
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', runInit);
} else {
  runInit();
}
